import React, { useEffect, useMemo, useState } from "react";
import { clamp } from "../../utils";

/* ---------------- token helpers ---------------- */

function parseTokens(query) {
  const tokens = (query || "").trim().split(/\s+/).filter(Boolean);
  const out = {
    level: "all",
    host: "",
    user: "",
    msg: "",
    actions: new Set(),
    rest: [],
  };

  for (const t of tokens) {
    const m = t.match(/^(\w+):(.+)$/);
    if (!m) {
      out.rest.push(t);
      continue;
    }
    const key = m[1].toLowerCase();
    const val = m[2];

    if (key === "level") out.level = val;
    else if (key === "host") out.host = val;
    else if (key === "user") out.user = val;
    else if (key === "msg") out.msg = val;
    else if (key === "action") out.actions.add(val);
    else out.rest.push(t);
  }
  return out;
}

function buildQuery({ level, host, user, msg, actions, rest }) {
  const parts = [];
  if (level && level !== "all") parts.push(`level:${level}`);
  if (host) parts.push(`host:${host}`);
  if (user) parts.push(`user:${user}`);
  if (msg) parts.push(`msg:${msg}`);

  // multiple action:* tokens are intended (OR in filter)
  actions.forEach((a) => parts.push(`action:${a}`));

  if (rest?.length) parts.push(...rest);
  return parts.join(" ").trim();
}

function ensureSet(s) {
  return s instanceof Set ? s : new Set(Array.isArray(s) ? s : []);
}

function toggle(setLike, v) {
  const n = new Set(ensureSet(setLike));
  n.has(v) ? n.delete(v) : n.add(v);
  return n;
}

/* ---------------- actions ---------------- */

const EVENT_ACTIONS = [
  { key: "event.create",   label: "Angelegt"  },
  { key: "event.update",   label: "Geändert"  },
  { key: "event.delete",   label: "Gelöscht"  },
  { key: "event.approve",  label: "Freigegeben" },
  { key: "event.reject",   label: "Abgelehnt", tone: "warn" },
  { key: "event.pending",  label: "Wartet"  },
];

const SECURITY_ACTIONS = [
  { key: "security.unauthorized",  label: "Nicht angemeldet",  tone: "warn"  },
  { key: "security.access_denied", label: "Kein Zugriff",     tone: "error" },
  { key: "security.login_failed",  label: "Login fehlgeschlagen",  tone: "error" },
  { key: "security.brute_force",   label: "Brute force",   tone: "error" },
  { key: "security.jwt_invalid",   label: "Token ungültig",   tone: "error" },
  { key: "security.rate_limited",  label: "Gedrosselt",  tone: "warn"  },
];

const AUTH_ACTIONS = [
  { key: "auth.login",           label: "Login"          },
  { key: "auth.logout",          label: "Logout"         },
  { key: "auth.token_refresh",   label: "Token erneuert"  },
  { key: "auth.session_expired", label: "Sitzung abgelaufen", tone: "warn" },
];

const MARKER_ACTIONS = [
  { key: "marker.create",        label: "Angelegt"        },
  { key: "marker.update",        label: "Geändert"        },
  { key: "marker.delete",        label: "Gelöscht"        },
  { key: "marker.approve",       label: "Freigegeben"       },
  { key: "marker.come",          label: "Kommt"          },
  { key: "marker.vote",          label: "Stimme"          },
  { key: "marker.vote.rejected", label: "Stimme abgelehnt", tone: "warn" },
];

const HOSTCODE_ACTIONS = [
  { key: "hostcode.create", label: "Angelegt" },
  { key: "hostcode.update", label: "Geändert" },
  { key: "hostcode.delete", label: "Gelöscht" },
  { key: "hostcode.redeem", label: "Eingelöst" },
];

const ADVERTISEMENT_ACTIONS = [
  { key: "advertisement.create",        label: "Angelegt"         },
  { key: "advertisement.update",        label: "Geändert"         },
  { key: "advertisement.delete",        label: "Gelöscht"         },
  { key: "advertisement.status_change", label: "Status geändert"  },
  { key: "advertisement.asset_upload",  label: "Datei hochgeladen"   },
  { key: "advertisement.submit",        label: "Eingereicht"         },
  { key: "advertisement.link_created",  label: "Link erstellt"   },
  { key: "advertisement.report_viewed", label: "Bericht angesehen"  },
];

const ARTIST_ACTIONS = [
  { key: "artist.create",       label: "Angelegt"        },
  { key: "artist.update",       label: "Geändert"        },
  { key: "artist.delete",       label: "Gelöscht"        },
  { key: "artist.spotify_sync", label: "Spotify-Abgleich"  },
];

const SESSION_ACTIONS = [
  { key: "session.create", label: "Angelegt"                },
  { key: "session.expire", label: "Abgelaufen", tone: "warn"  },
  { key: "session.delete", label: "Gelöscht"                },
];

/* ---------------- component ---------------- */

export default function LogsControls({
  styles,
  services,
  servicesLoading,
  service,
  setService,
  minutes,
  setMinutes,
  limit,
  setLimit,
  query,
  setQuery,
  setSelected,
}) {
  const parsed = useMemo(() => parseTokens(query), [query]);

  const [level, setLevel] = useState(parsed.level);
  const [host, setHost] = useState(parsed.host);
  const [user, setUser] = useState(parsed.user);
  const [msg, setMsg] = useState(parsed.msg);

  // ✅ ensure it's always a Set
  const [actions, setActions] = useState(() => ensureSet(parsed.actions));

  const [rest, setRest] = useState(parsed.rest);

  useEffect(() => {
    setLevel(parsed.level);
    setHost(parsed.host);
    setUser(parsed.user);
    setMsg(parsed.msg);
    setActions(ensureSet(parsed.actions));
    setRest(parsed.rest);
  }, [parsed]);

  useEffect(() => {
    const next = buildQuery({
      level,
      host,
      user,
      msg,
      actions: ensureSet(actions),
      rest,
    });
    if (next !== query) setQuery(next);
  }, [level, host, user, msg, actions, rest, query, setQuery]);

  const onClear = () => {
    setSelected(null);
    setLevel("all");
    setHost("");
    setUser("");
    setMsg("");
    setActions(new Set());
    setRest([]);
    setQuery("");
  };

  /* ---------------- layout styles ---------------- */

  const card = { ...styles.card, marginBottom: 12, padding: "16px 18px" };

  const sectionLabel = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 8,
  };

  const divider = {
    height: 1,
    background: "rgba(15,23,42,0.066)",
    margin: "14px 0",
  };

  const chipRow = { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" };

  const chip = (active, tone = "neutral") => {
    const colors = {
      error: { border: "rgba(220,38,38,0.6)",  bg: "rgba(220,38,38,0.12)", fg: "#dc2626"  },
      warn:  { border: "rgba(217,119,6,0.6)",  bg: "rgba(217,119,6,0.12)", fg: "#b45309"  },
      info:  { border: "rgba(0,108,253,0.6)",  bg: "rgba(0,108,253,0.12)", fg: "#0057d6"  },
    };
    const c = colors[tone] || { border: "rgba(15,23,42,0.12)", bg: "rgba(15,23,42,0.077)", fg: "#4b5563" };
    return {
      height: 26,
      padding: "0 10px",
      borderRadius: 6,
      fontSize: 11,
      fontWeight: active ? 600 : 400,
      display: "inline-flex",
      alignItems: "center",
      border: active ? `1px solid ${c.border}` : "1px solid rgba(15,23,42,0.077)",
      background: active ? c.bg : "transparent",
      color: active ? c.fg : "#6b7280",
      cursor: "pointer",
      userSelect: "none",
      transition: "all 120ms ease",
      whiteSpace: "nowrap",
    };
  };

  const levelChip = (active, tone) => ({
    ...chip(active, tone),
    height: 28,
    padding: "0 14px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
  });

  const isOn = (k) => ensureSet(actions).has(k);

  return (
    <div style={card}>
      {/* ── Top row: Service / Time / Limit / Filters / Clear ── */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ ...styles.label, minWidth: 160 }}>
          Service
          <select className="select-dark" value={service} onChange={(e) => setService(e.target.value)} disabled={servicesLoading} style={styles.input}>
            {services.map((s) => <option key={s} value={s}>{s || "(alle)"}</option>)}
          </select>
        </label>
        <label style={{ ...styles.label, width: 110 }}>
          Minuten
          <input type="number" value={minutes} onChange={(e) => setMinutes(clamp(+e.target.value || 60, 1, 1440))} style={styles.input} />
        </label>
        <label style={{ ...styles.label, width: 100 }}>
          Anzahl
          <input type="number" value={limit} onChange={(e) => setLimit(clamp(+e.target.value || 200, 10, 5000))} style={styles.input} />
        </label>
        <label style={{ ...styles.label, flex: 1, minWidth: 140 }}>
          Host-Code
          <input value={host} onChange={(e) => setHost(e.target.value.toUpperCase().slice(0, 8))} placeholder="VOGA…" style={styles.input} />
        </label>
        <label style={{ ...styles.label, flex: 1, minWidth: 140 }}>
          Nutzer
          <input value={user} onChange={(e) => setUser(e.target.value)} placeholder="admin1…" style={styles.input} />
        </label>
        <label style={{ ...styles.label, flex: 2, minWidth: 180 }}>
          Nachricht enthält
          <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="exception, /api, timeout…" style={styles.input} />
        </label>
        <button type="button" onClick={onClear} style={{ ...styles.button(false), alignSelf: "flex-end", opacity: 0.65 }}>
          Filter zurücksetzen
        </button>
      </div>

      <div style={divider} />

      {/* ── Level ── */}
      <div style={{ marginBottom: 14 }}>
        <div style={sectionLabel}>Level</div>
        <div style={chipRow}>
          {[["all","ALLE","info"],["info","INFO","info"],["warn","WARN","warn"],["error","ERROR","error"]].map(([val, lbl, tone]) => (
            <button key={val} type="button" style={levelChip(level === val, tone)} onClick={() => setLevel(val)}>{lbl}</button>
          ))}
        </div>
      </div>

      {/* ── Action groups ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px 20px" }}>
        {[
          { label: "Events",          list: EVENT_ACTIONS         },
          { label: "Sicherheit",        list: SECURITY_ACTIONS      },
          { label: "Anmeldung",            list: AUTH_ACTIONS          },
          { label: "Marker",         list: MARKER_ACTIONS        },
          { label: "Host-Codes",      list: HOSTCODE_ACTIONS      },
          { label: "Werbung",   list: ADVERTISEMENT_ACTIONS },
          { label: "Künstler",         list: ARTIST_ACTIONS        },
          { label: "Sitzungen",        list: SESSION_ACTIONS       },
        ].map(({ label, list }) => (
          <div key={label}>
            <div style={sectionLabel}>{label}</div>
            <div style={chipRow}>
              {list.map((a) => (
                <button key={a.key} type="button" style={chip(isOn(a.key), a.tone ?? "neutral")} onClick={() => setActions((s) => toggle(s, a.key))}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
